import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import { Copy, Mail, Send, CheckCircle } from 'lucide-react';

export interface RequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  docName: string;
  investmentName: string;
}

export const RequestModal: React.FC<RequestModalProps> = ({ isOpen, onClose, docName, investmentName }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showManualDetails, setShowManualDetails] = useState(false);
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const email = 'info@1stgroupcapital.co.za';
  const subject = `Inquiry Regarding Investment Document: ${investmentName}`;
  const body = `Dear ALTA-X Team,\n\nI am writing to request a copy of the following document for the '${investmentName}' investment opportunity:\n- ${docName}\n\nCould you please provide me with a copy at your earliest convenience?\n\nThank you for your assistance.\n\nSincerely,`;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  const CopyableInfoRow = ({ label, value, field }: { label: string; value: string; field: string; }) => (
    <div>
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
      <div className="mt-1 flex items-center justify-between rounded-md bg-gray-100/60 p-2">
        <p className="text-sm text-gray-800 font-mono break-all pr-2">{value}</p>
        <button 
          onClick={() => handleCopy(value, field)}
          className="flex-shrink-0 p-1.5 rounded-md hover:bg-gray-200 transition-colors"
          aria-label={`Copy ${label}`}
        >
          {copiedField === field ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <Copy className="h-5 w-5 text-gray-500" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Request Document" size="2xl">
      <div className="space-y-4">
        <p className="text-sm text-center text-muted-foreground">
          To receive "<strong>{docName}</strong>", please use an option below.
        </p>
        
        <div className="space-y-3">
          <a
            href={`mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`}
            className="inline-flex items-center justify-center w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-navy hover:bg-navy/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy transition-colors"
          >
            <Mail className="-ml-1 mr-2 h-5 w-5" />
            Open Default Email App
          </a>
          <a
            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodedSubject}&body=${encodedBody}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            <Send className="-ml-1 mr-2 h-5 w-5" />
            Compose with Gmail
          </a>
        </div>

        {!showManualDetails ? (
          <div className="text-center pt-1">
            <button 
              onClick={() => setShowManualDetails(true)} 
              className="text-xs text-primary hover:underline font-medium"
            >
              Or copy details manually
            </button>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink mx-3 text-xs font-semibold text-gray-400 uppercase">Manual Details</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>
            <CopyableInfoRow label="To" value={email} field="email" />
            <CopyableInfoRow label="Subject" value={subject} field="subject" />
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Body</span>
              <div className="relative mt-1 rounded-md bg-gray-100/60 p-2">
                <pre className="text-sm text-gray-800 font-mono whitespace-pre-wrap break-all">
                  {isBodyExpanded ? body.replace(/\n/g, '\n') : `${body.split('\n')[0]}...`}
                </pre>
                <button 
                  onClick={() => handleCopy(body, 'body')}
                  className="absolute top-1.5 right-1.5 flex-shrink-0 p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                  aria-label="Copy Body"
                >
                  {copiedField === 'body' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Copy className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                <div className="text-right -mr-1 -mb-1 mt-1">
                  <button
                    onClick={() => setIsBodyExpanded(!isBodyExpanded)}
                    className="text-xs text-primary hover:underline font-medium p-1"
                  >
                    {isBodyExpanded ? 'Show less' : 'Show more'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
